import ActionButton, { ActionButtonProps } from "@able/react/dist/ActionButton";
import IconButton from "@able/react/dist/IconButton";
import SectionHeader, { SectionHeaderProps } from "@able/react/dist/SectionHeader";
import React, { useEffect, useRef, useState } from "react";
import { AbleCommonProps } from "../Types";
import { classNames } from "../util/helpers";
import { SheetModalWrapper, useSheetModalContext } from "./context";
import "./style.scss";

type SheetModalFooterType = {
    buttonOne?: ActionButtonProps;
    buttonTwo?: ActionButtonProps;
};

export type SheetModalProps = {
    id?: string;
    title: string | SectionHeaderProps;
    className?: string;
    open?: boolean;
    onClose?: (e: any) => void;
    size?: "small" | 'medium' | "large";
    footerSetting?: SheetModalFooterType;
} & AbleCommonProps & React.PropsWithChildren;


export const SheetModalFooter = (props: { scrollend: boolean }) => {
    const { footerSetting } = useSheetModalContext();
    if (!footerSetting) return null;
    const { buttonOne, buttonTwo } = footerSetting;
    const { scrollend } = props;
    return <>
        {(buttonOne || buttonTwo) && <footer className={classNames('tui-sheet-modal-footer', scrollend && '--content-scrolled-end')}>
            {buttonOne && <ActionButton {...buttonOne} />}
            {buttonTwo && <ActionButton {...buttonTwo} />}
        </footer>}
    </>;
}

const SheetModalHeader = () => {
    const { title, developmentUrl, onClickClose, headingRef, headingId, currentLevel = 0, maxLevel = 0, previousLevel } = useSheetModalContext();
    let sectionHeaderProps: SectionHeaderProps = {
        variantLevel: "Level1",
        title: ""
    };
    if (typeof title === 'string') {
        sectionHeaderProps = {
            ...sectionHeaderProps,
            title: title,
        }
    } else if (typeof title === 'object') {
        sectionHeaderProps = {
            ...sectionHeaderProps,
            ...title,
        }
    } else {
        console.error("title should be either string or an Able SectionHeaderProp object.")
    }

    const showNavigationArrow = currentLevel > 1 && currentLevel <= maxLevel;

    return <header tabIndex={-1} className="ted-ui-sheet-modal-header" ref={headingRef}>
        {showNavigationArrow &&
            <IconButton className="ted-ui-sheet-arrow" icon="ChevronLeft" developmentUrl={developmentUrl} events={{ onClick: previousLevel }} data-testid="PreviousButton" />
        }
        <div id={headingId} className={classNames(showNavigationArrow && "ted-ui-sheet-header")}><SectionHeader {...sectionHeaderProps} /></div>
        <IconButton icon="Close" className="ted-ui-sheet-close" developmentUrl={developmentUrl} events={{ onClick: onClickClose }} data-testid="closeButton" />
    </header>;
}

/**
 * A Sheet Modal is top show content at in a floating sheet of on the document 
 *
 *
 * @version 1.0.0
 */

const SheetModal = (props: SheetModalProps) => {
    const { id, className, children, onClose, open, size = "large" } = props;
    const headingId = `${id ? id + '-' : 'tui-'}sheet-modal-heading`;
    const modalSizeClass = "tui-sheet-modal-" + size;
    const modalRef = useRef<HTMLDialogElement>(null);
    const modalContentRef = useRef<HTMLDivElement>(null);
    const headingRef = useRef(null);
    const [scrolled, setScrolled] = useState(false);
    const [scrollend, setScrollEnd] = useState(false)

    /** This Feature is no needed now keeping for future reference, this is close on backdrop click function */
    // const onClickBackdrop = (event: any) => {
    //     const rect = event.target.getBoundingClientRect();

    //     if (rect.left > event.clientX ||
    //         rect.right < event.clientX ||
    //         rect.top > event.clientY ||
    //         rect.bottom < event.clientY
    //     ) {
    //         event.target.close();
    //     }
    // }

    // useLayoutEffect(() => {
    //     if (modalRef.current) {
    //         if (closeOnBackDropClick) {
    //             modalRef.current.addEventListener("click", onClickBackdrop);
    //         } else {
    //             modalRef.current.removeEventListener("click", onClickBackdrop);
    //         }
    //     }
    //     return () => {
    //         if (modalRef.current) {
    //             modalRef.current.removeEventListener("click", onClickBackdrop);
    //         }
    //     }
    // }, [closeOnBackDropClick]);

    /** This Feature is no needed now keeping for future reference, this is close on backdrop click function */

    useEffect(() => {

        const onCloseDialog = (e: any) => {
            onClose && onClose(e);
        }

        const onResize = () => {
            if (modalContentRef.current)
                setScrollEnd(isScrollEnd(modalContentRef.current));
        }

        if (modalRef.current) {
            modalRef.current.removeEventListener("close", onCloseDialog);
            modalRef.current.addEventListener("close", onCloseDialog);

            if (open === true) {
                modalRef.current.showModal();
                setTimeout(() => {
                    if (headingRef.current)
                        (headingRef.current as HTMLElement).focus();
                    const refcontent = modalContentRef.current as HTMLDivElement;
                    setScrollEnd(refcontent.scrollHeight > refcontent.clientHeight);
                }, 200);
            } else {
                modalRef.current.close();
            }
        }

        window.addEventListener('resize', onResize)

        return () => {
            if (modalRef.current) {
                modalRef.current.removeEventListener("close", onCloseDialog);
            }
            window.removeEventListener('resize', onResize)
        }
    }, [open]);

    const closeModal = () => {
        if (modalRef && modalRef.current) {
            modalRef?.current.close();
        }
    }

    const isScrollEnd = (container: HTMLDivElement) => {
        const maxScrollPosition = container.scrollHeight - container.clientHeight;
        return Math.ceil(container.scrollTop) < maxScrollPosition;
    }

    const oNScrollContent = (event: React.MouseEvent<HTMLDivElement>) => {
        setScrolled(event.currentTarget.scrollTop > 0);
        setScrollEnd(isScrollEnd(event.currentTarget));
    }

    return <SheetModalWrapper {...props} onClickClose={closeModal} open={open} headingRef={headingRef} headingId={headingId}>
        <dialog ref={modalRef} id={id} aria-labelledby={headingId} className={classNames('tui-sheet-modal', modalSizeClass, className, scrolled && '--content-scrolled')}>
            <SheetModalHeader />
            <div tabIndex={0} ref={modalContentRef} onScroll={oNScrollContent} role="region" aria-label="scrollable" className={classNames("tui-sheet-modal-content")}>
                {children}
            </div>
            <SheetModalFooter scrollend={scrollend} />
        </dialog>
    </SheetModalWrapper>;
}

export default SheetModal;
